/**
 * DataTable.jsx - Reusable sortable data table.
 *
 * Renders a <table> with sortable column headers, clickable rows, and
 * smart cell formatting based on column type.
 *
 * Props:
 *   columns   {Array<Column>}  - Column definitions (see below).
 *   rows      {Array<Object>}  - Row data; each object keyed by column.key.
 *   onRow     {function}       - Click handler for a row; receives the row object.
 *   emptyText {string}         - Message shown when rows is empty (default: "No data").
 *
 * Column definition:
 *   {
 *     key:    string,          - Property name in the row object.
 *     label:  string,          - Header text.
 *     type:   string,          - "text" | "money" | "num" | "int" | "pct"
 *                                | "score" | "days" | "date" | "flag" | "badge"
 *                                Defaults to "text".
 *     render: {function}       - Optional custom renderer: (value, row) => node.
 *                                Overrides type-based formatting when provided.
 *     align:  string           - "left" | "center" | "right". Defaults based on type.
 *     sortable: boolean        - Whether column is sortable (default true).
 *   }
 *
 * Usage:
 *   <DataTable
 *     columns={[
 *       { key: "name", label: "Vendor" },
 *       { key: "amount", label: "Amount", type: "money" },
 *       { key: "status", label: "Status", type: "badge" },
 *     ]}
 *     rows={vendors}
 *     onRow={(v) => navigate(`/vendors/${v.id}`)}
 *   />
 */

import { useState, useMemo } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import Badge from "./Badge";
import Pager from "./Pager";

/* ------------------------------------------------------------------ */
/* Cell formatters                                                     */
/* ------------------------------------------------------------------ */

const CURRENCY = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const NUMBER = new Intl.NumberFormat("en-PH");

function formatCell(value, type) {
  if (value == null || value === "") return "\u2014";

  switch (type) {
    case "money":
      return CURRENCY.format(Number(value) || 0);

    case "num":
      return NUMBER.format(Number(value) || 0);

    case "int":
      return NUMBER.format(Math.round(Number(value) || 0));

    case "pct": {
      const n = Number(value);
      return isNaN(n) ? "\u2014" : `${n.toFixed(1)}%`;
    }

    case "score": {
      const s = Number(value);
      if (isNaN(s)) return "\u2014";
      const color =
        s >= 80 ? "var(--good)" : s >= 60 ? "var(--warn)" : "var(--bad)";
      return (
        <span style={{ fontWeight: 650, color }}>
          {s.toFixed(0)}
        </span>
      );
    }

    case "days": {
      const d = Number(value);
      if (isNaN(d)) return "\u2014";
      const color =
        d <= 7 ? "var(--good)" : d <= 14 ? "var(--warn)" : "var(--bad)";
      return (
        <span style={{ color }}>
          {d}d
        </span>
      );
    }

    case "date":
      if (!value) return "\u2014";
      try {
        const d = new Date(value);
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
      } catch {
        return String(value);
      }

    case "flag":
      return value ? (
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--good)",
          }}
        />
      ) : (
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--line)",
          }}
        />
      );

    case "badge":
      return <Badge text={String(value)} />;

    default:
      return String(value);
  }
}

/* ------------------------------------------------------------------ */
/* Default alignment per type                                          */
/* ------------------------------------------------------------------ */

function defaultAlign(type) {
  switch (type) {
    case "money":
    case "num":
    case "int":
    case "pct":
    case "score":
    case "days":
      return "right";
    case "flag":
      return "center";
    default:
      return "left";
  }
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function DataTable({
  columns,
  rows,
  onRow,
  emptyText = "No data",
  pageSize: initialPageSize = 20,
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  /* Toggle sort on a column */
  function handleSort(col) {
    if (col.sortable === false) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.key);
      setSortDir("asc");
    }
  }

  /* Sort rows */
  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    const dir = sortDir === "asc" ? 1 : -1;

    return [...rows].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];

      // Numeric types sort numerically
      const numericTypes = ["money", "num", "int", "pct", "score", "days"];
      if (col && numericTypes.includes(col.type)) {
        return (Number(va) - Number(vb)) * dir;
      }

      // Default string sort
      const sa = String(va ?? "").toLowerCase();
      const sb = String(vb ?? "").toLowerCase();
      return sa.localeCompare(sb) * dir;
    });
  }, [rows, sortKey, sortDir, columns]);

  /* Paginate sorted rows */
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = sortedRows.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  /* Reset to page 1 when data changes */
  useMemo(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  /* Handle page size change */
  function handlePageSizeChange(newSize) {
    setPageSize(newSize);
    setPage(1);
  }

  /* Handle page change */
  function handlePageChange(newPage) {
    setPage(Math.max(1, Math.min(newPage, totalPages)));
  }

  if (!columns || columns.length === 0) return null;

  return (
    <>
      <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table style={{ minWidth: columns.length * 120, width: "auto", tableLayout: "auto" }}>
          <thead>
            <tr>
              {columns.map((col) => {
                const isSorted = sortKey === col.key;
                const align = col.align || defaultAlign(col.type);
                const canSort = col.sortable !== false;

                return (
                  <th
                    key={col.key}
                    onClick={canSort ? () => handleSort(col) : undefined}
                    style={{
                      textAlign: align,
                      cursor: canSort ? "pointer" : "default",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      {col.label}
                      {isSorted &&
                        (sortDir === "asc" ? (
                          <ChevronUp size={12} />
                        ) : (
                          <ChevronDown size={12} />
                        ))}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: 34, color: "var(--ink-3)" }}
                >
                  {emptyText}
                </td>
              </tr>
            ) : (
              pageRows.map((row, i) => (
                <tr
                  key={row.id ?? i}
                  className={onRow ? "clickable" : undefined}
                  onClick={onRow ? () => onRow(row) : undefined}
                >
                  {columns.map((col) => {
                    const align = col.align || defaultAlign(col.type);
                    const value = row[col.key];

                    return (
                      <td key={col.key} style={{ textAlign: align }}>
                        {col.render
                          ? col.render(value, row)
                          : formatCell(value, col.type)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pager
        page={safePage}
        totalPages={totalPages}
        totalRows={sortedRows.length}
        pageSize={pageSize}
        onPage={handlePageChange}
        onPageSize={handlePageSizeChange}
      />
    </>
  );
}
